/**
 * ゲーミフィケーション データAPI モック層（Phase 4 / 設計書 §6.3・§11・§F.2）
 *
 * ワーカーフロントには現状 `liff.mock.ts`（LIFF SDK モック）しか無く、データAPI用の
 * モック層が存在しないため（§6.3 ⚠️ / §7「モック」）、本機能のデモにはここで新設する。
 *
 * 方針:
 * - 既存 `liff.mock.ts` と同じトグル規約に合わせる
 *     1) ビルド時ゲート: REACT_APP_USE_GAMIFICATION_MOCK === 'true'
 *     2) ランタイムゲート: URL ?gami-mock=true / ?gami-mock=false（localStorage に永続）
 *        もしくは既存の ?liff-mock=true でも有効化（LIFF モック運用とまとめてデモできるよう相乗り）
 * - 本番ビルドでは REACT_APP_USE_GAMIFICATION_MOCK を立てない限り **常に無効**。
 * - api.ts のゲーミフィケーション系メソッドのみが本モックを参照する（他APIには一切影響しない）。
 *
 * 返却スキーマは付録D.1（sharedTypes）/ D.2 準拠。color は付録C 実測値。
 */

import {
  TierObject,
  RankObject,
  EngagementObject,
  RadarObject,
  SkillObject,
  GlobalRankPublicObject,
  StoreRankPublicObject,
  JobPredictionObject,
  WorkerGamificationBundle,
  SpecialOffer,
  SpecialOfferRespondStatus,
  UnseenSkillsResponse,
  MarkSeenResponse,
} from '../types';

const GAMI_MOCK_KEY = 'gami-mock-enabled';

/** ビルド時にモックが許可されているか（REACT_APP_USE_GAMIFICATION_MOCK=true） */
export const isGamificationMockAllowed = (): boolean =>
  process.env.REACT_APP_USE_GAMIFICATION_MOCK === 'true';

/**
 * ゲーミフィケーション データモックを有効化すべきか。
 * 許可されていなければ常に false（本番安全）。許可下では URL/localStorage で opt-in。
 * `?liff-mock=true` 運用にも相乗りして有効化する（デモ運用の利便性）。
 */
export const isGamificationMockEnabled = (): boolean => {
  if (!isGamificationMockAllowed()) return false;

  const params = new URLSearchParams(window.location.search);
  const urlParam = params.get('gami-mock');
  if (urlParam === 'true') {
    localStorage.setItem(GAMI_MOCK_KEY, 'true');
    return true;
  }
  if (urlParam === 'false') {
    localStorage.removeItem(GAMI_MOCK_KEY);
    return false;
  }

  if (localStorage.getItem(GAMI_MOCK_KEY) === 'true') return true;

  // LIFF モック運用に相乗り（同じデモセッションで一括 ON にできる）
  if (params.get('liff-mock') === 'true' || localStorage.getItem('liff-mock-enabled') === 'true') {
    return true;
  }
  return false;
};

export const disableGamificationMock = (): void => {
  localStorage.removeItem(GAMI_MOCK_KEY);
};

// ---- tier マスタ（付録C 色）----
const TIERS: Record<string, TierObject> = {
  rookie:   { tier_id: 'tier-rookie',   code: 'rookie',   name: 'ルーキー', color: '#57A665', sort_order: 0 },
  bronze:   { tier_id: 'tier-bronze',   code: 'bronze',   name: 'ブロンズ', color: '#B7553C', sort_order: 1 },
  silver:   { tier_id: 'tier-silver',   code: 'silver',   name: 'シルバー', color: '#BDC2C4', sort_order: 2 },
  gold:     { tier_id: 'tier-gold',     code: 'gold',     name: 'ゴールド', color: '#EEB812', sort_order: 3 },
  platinum: { tier_id: 'tier-platinum', code: 'platinum', name: 'プラチナ', color: '#8095A8', sort_order: 4 },
};

// ---- サンプルデータ（付録A.8 の数値例に概ね整合）----
const mockRank: RankObject = {
  tier: TIERS.silver,
  total_shifts: 24,
  current_hourly_wage: 1280,
  is_manual_override: false,
  promoted_at: '2026-04-12T10:00:00',
  updated_at: '2026-06-10T03:00:00',
};

const mockEngagement: EngagementObject = {
  retention_rate: 82.3,
  trust_score: 83.34,
  completed_shifts: 12,
  cancelled_shifts: 1,
  no_show_shifts: 0,
  last_recomputed_at: '2026-06-14T03:00:00',
};

// 正準順 対人/統率/規律/知識/実行（付録A.8）
const mockRadar: RadarObject = {
  interpersonal: 78.83,
  leadership: 51.75,
  discipline: 88.66,
  knowledge: 58.2,
  execution: 79.8,
  level: 24,
  is_provisional: false,
};

const mockSkills: SkillObject[] = [
  {
    skill_id: 'sk-1', skill_definition_id: 'sd-1', name: 'レジ締め', tier: TIERS.silver,
    slot_index: 0, level: 2, acquired_at: '2026-05-20T18:30:00', granted_via: 'approval',
    granted_by_attendance_id: 'wa-1',
  },
  {
    skill_id: 'sk-2', skill_definition_id: 'sd-2', name: '新人教育', tier: TIERS.silver,
    slot_index: 1, level: 1, acquired_at: '2026-05-28T21:00:00', granted_via: 'approval',
    granted_by_attendance_id: 'wa-2',
  },
  {
    skill_id: 'sk-3', skill_definition_id: 'sd-3', name: 'ホール接客', tier: TIERS.bronze,
    slot_index: 0, level: 3, acquired_at: '2026-03-15T20:00:00', granted_via: 'approval',
    granted_by_attendance_id: 'wa-3',
  },
  {
    skill_id: 'sk-4', skill_definition_id: 'sd-4', name: '開店準備', tier: TIERS.crew,
    slot_index: 0, level: 2, acquired_at: '2026-02-10T09:00:00', granted_via: 'approval',
    granted_by_attendance_id: 'wa-4',
  },
];

// 公開マスク版（user_id は実ワイヤに無い。global_score / total_worked_minutes / breakdown はマスク）
const mockGlobalRankPublic: GlobalRankPublicObject = {
  tier: TIERS.gold,
  store_count: 3,
  total_worked_days: 86,
};

// 店舗内ランク 公開マスク版（GET /api/store-users/{id}/rank。wage / override はマスク）
const mockStoreRankPublic: StoreRankPublicObject = {
  tier: mockRank.tier,
  total_shifts: mockRank.total_shifts,
};

const mockBundle: WorkerGamificationBundle = {
  rank: mockRank,
  engagement: mockEngagement,
  parameters: mockRadar,
  skills: mockSkills,
  flags: { skills_ready: true },
};

// 予測報酬は実支払予測の総額（min == max の単一値）。
const mockPrediction: JobPredictionObject = {
  predicted_reward_min: 12800,
  predicted_reward_max: 12800,
  predicted_skills: [
    { name: 'ホール接客', tier: TIERS.silver },
    { name: 'レジ操作', tier: TIERS.bronze },
  ],
  required_tier: TIERS.crew,
  estimated: true,
};

// ---- Phase 5: 特別オファー / 招待状 + GET SKILL 未読 ----
// 受信箱（諾否で status/responded_at が変わるのを再現するため可変 let）。
let mockOffers: SpecialOffer[] = [
  {
    // 招待状 INVITATION（§6.3 p.20: Official Career Talk / GIFT TICKET / 店長からの招待状）。
    offer_id: 'offer-1',
    store_user_id: 'su-1',
    store_id: 'store-1',
    offer_type: 'invitation',
    title: '店長キャリア面談へのご招待',
    message_content:
      'いつも頼りにしています。シルバーランク到達おめでとうございます！\nあなたの今後のキャリアについて、店長と直接お話しする「Official Career Talk」にご招待します。',
    reward_content: 'GIFT TICKET（特別時給 +200円 / 交通費全額支給）',
    status: 'sent',
    line_message_log_id: 'log-1',
    sent_at: '2026-06-15T10:00:00',
    responded_at: null,
    created_at: '2026-06-15T10:00:00',
    updated_at: '2026-06-15T10:00:00',
  },
  {
    offer_id: 'offer-2',
    store_user_id: 'su-1',
    store_id: 'store-1',
    offer_type: 'special',
    title: '【特別オファー】レジ締めリーダー募集',
    message_content: 'レジ締めスキルを活かして、リーダーとして活躍しませんか？',
    reward_content: 'リーダー手当 +300円/h',
    status: 'sent',
    line_message_log_id: 'log-2',
    sent_at: '2026-06-12T09:00:00',
    responded_at: null,
    created_at: '2026-06-12T09:00:00',
    updated_at: '2026-06-12T09:00:00',
  },
  {
    // 応答済み招待状（諾否後の表示確認用。responded_at / status=accepted）。
    offer_id: 'offer-3',
    store_user_id: 'su-1',
    store_id: 'store-1',
    offer_type: 'invitation',
    title: '新人トレーナー就任のご相談',
    message_content: '新人教育スキルを評価して、トレーナーとしてのご就任を打診させてください。',
    reward_content: 'トレーナー手当 +250円/h',
    status: 'accepted',
    line_message_log_id: 'log-3',
    sent_at: '2026-06-08T09:00:00',
    responded_at: '2026-06-09T12:30:00',
    created_at: '2026-06-08T09:00:00',
    updated_at: '2026-06-09T12:30:00',
  },
];

// GET SKILL 未読スキル（mark-seen で既読化されて空になるのを再現するため可変 let）。
// 複数同時付与（承認時に2件付与）も演出で確認できるよう2件用意。
let mockUnseenSkills: SkillObject[] = [
  {
    skill_id: 'sk-5', skill_definition_id: 'sd-5', name: 'クレーム対応', tier: TIERS.silver,
    slot_index: 2, level: 1, acquired_at: '2026-06-14T20:00:00', granted_via: 'approval',
    granted_by_attendance_id: 'wa-5',
  },
  {
    skill_id: 'sk-6', skill_definition_id: 'sd-6', name: '発注管理', tier: TIERS.gold,
    slot_index: 0, level: 1, acquired_at: '2026-06-14T20:00:00', granted_via: 'approval',
    granted_by_attendance_id: 'wa-5',
  },
];

// ---- モック応答ヘルパ（api.ts の ApiResponse 形に合わせる）----
const ok = <T>(data: T) => ({ success: true as const, data });

export const gamificationMock = {
  getSkills: async (_storeUserId: string) => ok({ skills: mockSkills, level: mockRadar.level }),
  getSkillParameters: async (_storeUserId: string) => ok<RadarObject>(mockRadar),
  getGamificationBundle: async (_storeUserId: string) => ok<WorkerGamificationBundle>(mockBundle),
  // 実ワイヤに user_id は無いため注入しない（型と一致させる）。
  getGlobalRank: async (_userId: string) => ok<GlobalRankPublicObject>(mockGlobalRankPublic),
  getStoreUserRank: async (_storeUserId: string) => ok<StoreRankPublicObject>(mockStoreRankPublic),
  getJobPrediction: async (_shiftId: string, _storeUserId: string) =>
    ok<JobPredictionObject>(mockPrediction),

  // Phase 5
  getSpecialOffers: async (_storeUserId: string) => ok<SpecialOffer[]>([...mockOffers]),
  respondSpecialOffer: async (
    _storeUserId: string,
    offerId: string,
    status: SpecialOfferRespondStatus,
  ) => {
    const now = new Date().toISOString();
    mockOffers = mockOffers.map((o) =>
      o.offer_id === offerId ? { ...o, status, responded_at: now, updated_at: now } : o,
    );
    const updated = mockOffers.find((o) => o.offer_id === offerId)!;
    return ok<SpecialOffer>(updated);
  },
  getUnseenSkills: async (storeUserId: string) =>
    ok<UnseenSkillsResponse>({ store_user_id: storeUserId, unseen: [...mockUnseenSkills] }),
  markSkillsSeen: async (_storeUserId: string, skillIds: string[] = []) => {
    // 冪等: 空配列なら全未読を既読化、指定があればその ID のみ。
    const before = mockUnseenSkills.length;
    mockUnseenSkills =
      skillIds.length === 0
        ? []
        : mockUnseenSkills.filter((s) => !skillIds.includes(s.skill_id));
    const marked = before - mockUnseenSkills.length;
    return ok<MarkSeenResponse>({ marked });
  },
};
